import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Plus,
  Edit2,
  Trash2,
  Loader2,
  Check,
  X,
  AlertTriangle,
  Crown,
  Sparkles,
  Zap,
  Package,
  Users,
  DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminSubscriptionApi } from './adminApi';

const PLAN_ICONS = {
  free: Package,
  basic: Zap,
  pro: Sparkles,
  enterprise: Crown,
};

const BILLING_INTERVALS = ['month', 'year'];
const CURRENCIES = ['USD', 'EUR', 'GBP'];

const formatCurrency = (amount, currency = 'USD') => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
};

const DEFAULT_PLAN = {
  name: '',
  slug: '',
  description: '',
  price: '',
  currency: 'USD',
  billing_interval: 'month',
  features: {},
  is_active: true,
  trial_days: 0,
  sort_order: 0,
};

const COMMON_FEATURES = [
  { key: 'campaigns', label: 'Campaigns', type: 'number' },
  { key: 'players_per_campaign', label: 'Players per Campaign', type: 'number' },
  { key: 'storage_gb', label: 'Storage (GB)', type: 'number' },
  { key: 'custom_maps', label: 'Custom Maps', type: 'boolean' },
  { key: 'ai_assistance', label: 'AI Assistance', type: 'boolean' },
  { key: 'audio_library', label: 'Audio Library', type: 'boolean' },
  { key: 'advanced_combat', label: 'Advanced Combat', type: 'boolean' },
  { key: 'api_access', label: 'API Access', type: 'boolean' },
  { key: 'priority_support', label: 'Priority Support', type: 'boolean' },
  { key: 'custom_branding', label: 'Custom Branding', type: 'boolean' },
];

function PlanFormModal({ open, onClose, plan, onSave, loading, title, hasActiveSubscribers }) {
  const [form, setForm] = useState(DEFAULT_PLAN);

  useEffect(() => {
    if (plan) {
      setForm({
        ...DEFAULT_PLAN,
        ...plan,
        price: plan.price?.toString() || '',
        features: plan.features || {},
      });
    } else {
      setForm(DEFAULT_PLAN);
    }
  }, [plan, open]);

  const updateField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const updateFeature = (key, value) => {
    setForm((f) => ({ ...f, features: { ...f.features, [key]: value } }));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.slug.trim()) { toast.error('Slug is required'); return; }
    const price = parseFloat(form.price);
    if (isNaN(price) || price < 0) { toast.error('Enter a valid price'); return; }

    onSave({ ...form, price });
  };

  const isEditing = !!plan;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>{title || (isEditing ? 'Edit Plan' : 'Create Plan')}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update plan configuration.' : 'Create a new subscription plan.'}
            {hasActiveSubscribers && (
              <span className="block mt-1 text-orange-600 font-medium">
                This plan has active subscribers. Price changes will not affect existing subscriptions.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Plan Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Pro Plan"
                className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Slug</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="pro"
                disabled={isEditing}
                className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Plan description for marketing pages..."
              rows={2}
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Price</label>
              <input
                type="number"
                value={form.price}
                onChange={(e) => updateField('price', e.target.value)}
                step="0.01"
                min="0"
                disabled={isEditing && hasActiveSubscribers}
                className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Currency</label>
              <select
                value={form.currency}
                onChange={(e) => updateField('currency', e.target.value)}
                className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Billing Interval</label>
              <select
                value={form.billing_interval}
                onChange={(e) => updateField('billing_interval', e.target.value)}
                className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {BILLING_INTERVALS.map((i) => <option key={i} value={i}>{i}ly</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Trial Days</label>
              <input
                type="number"
                value={form.trial_days}
                onChange={(e) => updateField('trial_days', parseInt(e.target.value, 10) || 0)}
                min="0"
                className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Sort Order</label>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => updateField('sort_order', parseInt(e.target.value, 10) || 0)}
                className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* Feature Flags */}
          <div>
            <label className="block text-sm font-medium mb-2">Feature Flags</label>
            <div className="space-y-2 p-3 bg-muted/50 rounded-lg border">
              {COMMON_FEATURES.map((feat) => (
                <div key={feat.key} className="flex items-center justify-between">
                  <label className="text-sm">{feat.label}</label>
                  {feat.type === 'boolean' ? (
                    <button
                      onClick={() => updateFeature(feat.key, !form.features[feat.key])}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        form.features[feat.key] ? 'bg-primary' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                          form.features[feat.key] ? 'translate-x-[18px]' : 'translate-x-[3px]'
                        }`}
                      />
                    </button>
                  ) : (
                    <input
                      type="number"
                      value={form.features[feat.key] ?? ''}
                      onChange={(e) => updateFeature(feat.key, e.target.value === '' ? null : parseInt(e.target.value, 10))}
                      min="0"
                      placeholder="-1 = unlimited"
                      className="w-32 h-7 px-2 text-xs rounded-md border border-input bg-background"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEditing ? 'Save Changes' : 'Create Plan'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [deactivateLoading, setDeactivateLoading] = useState(false);

  // Compare mode
  const [compareMode, setCompareMode] = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await adminSubscriptionApi.listPlans();
      setPlans(res.data || res.plans || res || []);
    } catch (err) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleCreate = async (data) => {
    setSaving(true);
    try {
      const result = await adminSubscriptionApi.createPlan({
        ...data,
        sync_providers: true, // Auto-create on Stripe + PayPal
      });
      const synced = [];
      if (result.stripe_price_id) synced.push('Stripe');
      if (result.paypal_plan_id) synced.push('PayPal');
      const syncMsg = synced.length > 0
        ? ` (synced to ${synced.join(' & ')})`
        : ' (provider sync pending — configure API keys)';
      toast.success(`Plan created successfully${syncMsg}`);
      setCreateModal(false);
      fetchPlans();
    } catch (err) {
      toast.error('Failed to create plan');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (data) => {
    setSaving(true);
    try {
      const result = await adminSubscriptionApi.updatePlan(selectedPlan.id, data);
      const hasProviders = result.stripe_product_id || result.paypal_plan_id;
      const nameOrDescChanged = data.name !== selectedPlan.name || data.description !== selectedPlan.description;
      const syncMsg = hasProviders && nameOrDescChanged ? ' (synced to payment providers)' : '';
      toast.success(`Plan updated successfully${syncMsg}`);
      setEditModal(false);
      setSelectedPlan(null);
      fetchPlans();
    } catch (err) {
      toast.error(err.message || 'Failed to update plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    setDeactivateLoading(true);
    try {
      await adminSubscriptionApi.deactivatePlan(selectedPlan.id);
      const hasProviders = selectedPlan.stripe_price_id || selectedPlan.paypal_plan_id;
      toast.success(
        hasProviders
          ? 'Plan deactivated locally and on payment providers. Existing subscribers keep their plan.'
          : 'Plan deactivated. No new subscriptions will be created.'
      );
      setDeactivateModal(false);
      setSelectedPlan(null);
      fetchPlans();
    } catch (err) {
      toast.error(err.message || 'Failed to deactivate plan');
    } finally {
      setDeactivateLoading(false);
    }
  };

  const openEdit = (plan) => {
    setSelectedPlan(plan);
    setEditModal(true);
  };

  const openDeactivate = (plan) => {
    setSelectedPlan(plan);
    setDeactivateModal(true);
  };

  const activePlans = plans.filter((p) => p.is_active !== false);
  const inactivePlans = plans.filter((p) => p.is_active === false);

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">Plans</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-3">
                  <div className="h-6 w-24 bg-muted rounded" />
                  <div className="h-8 w-16 bg-muted rounded" />
                  <div className="h-4 w-full bg-muted rounded" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Plans</h1>
          <p className="text-sm text-muted-foreground">Manage subscription plans and features</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setCompareMode(!compareMode)}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${compareMode ? 'bg-primary text-primary-foreground' : 'border-input bg-background hover:bg-accent'}`}
          >
            Compare Plans
          </button>
          <button
            onClick={() => setCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Plan
          </button>
        </div>
      </div>

      {/* Plan Cards */}
      {!compareMode ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {activePlans.map((plan) => {
              const PlanIcon = PLAN_ICONS[plan.slug] || Package;
              return (
                <Card key={plan.id} className="relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <PlanIcon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{plan.name}</CardTitle>
                          <p className="text-xs text-muted-foreground font-mono">{plan.slug}</p>
                        </div>
                      </div>
                      <Badge variant={plan.is_active !== false ? 'default' : 'secondary'}>
                        {plan.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-3xl font-bold">
                        {formatCurrency(plan.price, plan.currency)}
                        <span className="text-sm font-normal text-muted-foreground">
                          /{plan.billing_interval || 'month'}
                        </span>
                      </p>
                      {plan.description && (
                        <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
                      )}
                    </div>

                    {plan.trial_days > 0 && (
                      <Badge variant="outline">{plan.trial_days}-day trial</Badge>
                    )}

                    {/* Provider sync status */}
                    {plan.price > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        <Badge variant={plan.stripe_price_id ? 'default' : 'outline'} className="text-xs">
                          {plan.stripe_price_id ? '✓ Stripe' : '○ Stripe'}
                        </Badge>
                        <Badge variant={plan.paypal_plan_id ? 'default' : 'outline'} className="text-xs">
                          {plan.paypal_plan_id ? '✓ PayPal' : '○ PayPal'}
                        </Badge>
                      </div>
                    )}

                    {/* Subscriber count */}
                    {plan.subscriber_count != null && (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Users className="h-3.5 w-3.5" />
                        {plan.subscriber_count} subscriber{plan.subscriber_count !== 1 ? 's' : ''}
                      </div>
                    )}

                    {/* Features */}
                    {plan.features && Object.keys(plan.features).length > 0 && (
                      <div className="space-y-1.5 pt-2 border-t">
                        {Object.entries(plan.features).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">{key.replace(/_/g, ' ')}</span>
                            <span className="font-medium">
                              {typeof value === 'boolean' ? (
                                value ? <Check className="h-3.5 w-3.5 text-green-600" /> : <X className="h-3.5 w-3.5 text-gray-400" />
                              ) : (
                                value === -1 ? 'Unlimited' : value
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <button
                        onClick={() => openEdit(plan)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors"
                      >
                        <Edit2 className="h-3 w-3" />
                        Edit
                      </button>
                      <button
                        onClick={() => openDeactivate(plan)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-red-200 text-red-700 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="h-3 w-3" />
                        Deactivate
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Inactive Plans */}
          {inactivePlans.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 text-muted-foreground">Inactive Plans</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {inactivePlans.map((plan) => {
                  const PlanIcon = PLAN_ICONS[plan.slug] || Package;
                  return (
                    <Card key={plan.id} className="opacity-60">
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-2">
                          <PlanIcon className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <CardTitle className="text-base">{plan.name}</CardTitle>
                            <p className="text-xs text-muted-foreground font-mono">{plan.slug}</p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xl font-bold text-muted-foreground">
                          {formatCurrency(plan.price, plan.currency)}/{plan.billing_interval || 'month'}
                        </p>
                        <Badge variant="secondary" className="mt-2">Inactive</Badge>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : (
        /* Comparison Table */
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Plan Comparison</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="p-3 text-left font-medium text-muted-foreground">Feature</th>
                  {activePlans.map((plan) => (
                    <th key={plan.id} className="p-3 text-center font-semibold">
                      {plan.name}
                      <div className="text-xs font-normal text-muted-foreground">
                        {formatCurrency(plan.price, plan.currency)}/{plan.billing_interval || 'mo'}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3 text-muted-foreground">Trial Period</td>
                  {activePlans.map((plan) => (
                    <td key={plan.id} className="p-3 text-center">
                      {plan.trial_days ? `${plan.trial_days} days` : '-'}
                    </td>
                  ))}
                </tr>
                {COMMON_FEATURES.map((feat) => (
                  <tr key={feat.key} className="border-b">
                    <td className="p-3 text-muted-foreground">{feat.label}</td>
                    {activePlans.map((plan) => {
                      const val = plan.features?.[feat.key];
                      return (
                        <td key={plan.id} className="p-3 text-center">
                          {val == null ? (
                            <span className="text-gray-300">-</span>
                          ) : typeof val === 'boolean' ? (
                            val ? <Check className="h-4 w-4 text-green-600 mx-auto" /> : <X className="h-4 w-4 text-gray-400 mx-auto" />
                          ) : (
                            <span className="font-medium">{val === -1 ? 'Unlimited' : val}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr>
                  <td className="p-3 text-muted-foreground">Subscribers</td>
                  {activePlans.map((plan) => (
                    <td key={plan.id} className="p-3 text-center font-medium">
                      {plan.subscriber_count ?? '-'}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Create Plan Modal */}
      <PlanFormModal
        open={createModal}
        onClose={() => setCreateModal(false)}
        plan={null}
        onSave={handleCreate}
        loading={saving}
        title="Create New Plan"
      />

      {/* Edit Plan Modal */}
      <PlanFormModal
        open={editModal}
        onClose={() => { setEditModal(false); setSelectedPlan(null); }}
        plan={selectedPlan}
        onSave={handleUpdate}
        loading={saving}
        title="Edit Plan"
        hasActiveSubscribers={selectedPlan?.subscriber_count > 0}
      />

      {/* Deactivate Confirmation Modal */}
      <Dialog open={deactivateModal} onOpenChange={(v) => { if (!v) { setDeactivateModal(false); setSelectedPlan(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Deactivate Plan
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to deactivate <span className="font-semibold">{selectedPlan?.name}</span>?
              {selectedPlan?.subscriber_count > 0 && (
                <span className="block mt-2 text-orange-600 font-medium">
                  Warning: This plan has {selectedPlan.subscriber_count} active subscriber{selectedPlan.subscriber_count !== 1 ? 's' : ''}.
                  Existing subscriptions will continue until their period ends.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Deactivated plans will no longer be available for new subscriptions.
            This action can be reversed by re-activating the plan.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              onClick={() => { setDeactivateModal(false); setSelectedPlan(null); }}
              className="px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDeactivate}
              disabled={deactivateLoading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {deactivateLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Deactivate Plan
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
